import React from 'react';
import ReactDOM from 'react-dom';
import App from './App';
import { setDataStoreWithInitialResponseData } from './data/DataStore';
import * as utility from './utility';
import * as ContentByQueryUtilities from './lib/ContentByQueryUtilities';
import { getContentByQueryOtsAvailability } from './data/OtsRequests';
import { requestFeatureExperimentation, logExperimentExposure } from './data/FeatureExperimentation';
import { setWebAuthCookieIfNotExist } from './lib/CookieUtilities.js';
/* global novSelect */  // This is an eslint directive to not complain about the novSelect global references

/**
 * This file is the core bootstrapping logic and API for the Select V3 UI
 * It creates additional public methods available on the global novSelect object
 */

/**
 * Utility function to get a query object given an ISBN
 */
function isbnToQuery(isbn) {
	return {
		"ClientIdentifier": isbn,
		"ISBN": isbn,
		"version": ContentByQueryUtilities.NS_VERSION
	}
}

/**
 *
 * @param {string} id The id of the DOM node where React is mounted
 * This function is used by the console harness to unmount the React instance
 * so that a new instance can be created without issues
 */
function clearReact(id) {
	ReactDOM.unmountComponentAtNode(document.getElementById(id));
}

/**
 * Builds the fully qualified URL for the "/Data/ContentByQuery" API endpoint. This endpoint provides
 * the initial payload of data for the initial query.
 *
 *
 * @param {string} profile The customer Profile ID.
 * @param {string} password The encrypted customer Password.
 * @param {string} siteIdentifier Optional. Supports integration using a single profile/password on the client side.
 * @param {Object} query An object containing the customer query (ClientIdentifier, ISBN, UPC, Version).
 * @returns The URL of the "ContentByQuery" API endpoint.
 */
function buildContentByQueryUrl(profile, password, siteIdentifier, query) {
	const queryParams = ContentByQueryUtilities.buildDefaultQueryStringParameters(query);

	const optionalCustomLinks = ContentByQueryUtilities.buildCustomLinkQueryStringParameters(novSelect.config.CustomLinks, novSelect.config.CustomLinkReplacementRegexes, window.location.href);

	return (
		utility.getNovSchema() +
		novSelect.baseUrl +
		"/Data/ContentByQuery?profile=" +
		profile +
		"&password=" +
		password +
		"&siteToken=" +
		siteIdentifier +
		queryParams +
		"&ClientIdentifier=" +
		encodeURI(query.ClientIdentifier) +
		"&callback=novSelect.dataLoaded" +
		optionalCustomLinks);
}

export function wasDataReturned(data) {
	return data && data.FeatureCount > 0;
}

export function createDataLoadedCallback(config, baseUrl, query, successCallback = null, optionalTransform = (data) => data) {
	/**
	 * The main entry point of the NoveList Select V3 application.
	 *
	 * @param {Object} data The ContentByQuery response data.
	 */

	return function (data) {
		/**
		   * It is a little weird that we have two identical if statements, but the success callback should always run if provided,
		   * and it should run after the data store is populated, but before the initial app render, hence two if statements
		   */
		if (wasDataReturned(data)) {
			setDataStoreWithInitialResponseData(data, config, baseUrl);
			// What? Why would you do a get without saving the return value...
			// Using this to get the request started early so other calls to it can get the promise returned
			getContentByQueryOtsAvailability(data);
		}

		const simplifiedJson = optionalTransform(data);

		if (successCallback) {
			logExperimentExposure('callback-function-users');
			successCallback(simplifiedJson);
		}

		if (wasDataReturned(data)) {
			requestFeatureExperimentation(() => {
				// Prefer customer provided client identifier
				let clientIdentifier = (query && query.ClientIdentifier) || data.ClientIdentifier;

				const appContainer = utility.getAppMountElement(clientIdentifier);
				ReactDOM.render(<App />, appContainer);
				setWebAuthCookieIfNotExist();
			});
		}
	}
}
/**
 * The API we recommend our customers use. Fetches the content for the requested query.
 *
 * Supports an optional callback that provides a simplified query response.
 * NOTE: The callback response data is specifically formatted for this API.
 *
 * @param {Object} query An object containing the customer query (ClientIdentifier, ISBN, UPC, Version)
 * @param {string} profile The customer Profile ID.
 * @param {string} password The encrypted customer Password.
 * @param {successCallback} successCallback Optional. Provides a simplified response which some customers use.
 * @param {string} template Deprecated. This was used in the V2 UI.
 */
function contentForQuery(query, profile, password, successCallback) {
	tryContentByQuery(profile, password, null, query, successCallback, utility.transformDataForContentForQuery);
}

/**
 * Older API that some customers use. Fetches the content for the requested query.
 *
 * Supports an optional callback that provides a simplified query response.
 * NOTE: The callback response data is specifically formatted for this API.
 *
 * @param {string} isbn The ISBN of the title the user is viewing
 * @param {string} profile The customer Profile ID.
 * @param {string} password The encrypted customer Password.
 * @param {successCallback} successCallback Optional. Provides a simplified response which some customers use.
 * @param {string} template Deprecated. This was used in the V2 UI.
 */
function contentForISBN(isbn, profile, password, successCallback) {
	tryContentByQuery(profile, password, null, isbnToQuery(isbn), successCallback, utility.transformDataForContentForIsbn);
}

function dataForISBN(profile, password, appendTo, isbn, templateName, successCallback) {
	tryContentByQuery(profile, password, null, isbnToQuery(isbn), successCallback, (data) => data);
}

function dataForQuery(profile, password, appendTo, query, templateName, successCallback) {
	tryContentByQuery(profile, password, null, query, successCallback, (data) => data);
}

/**
 * NOT CURRENTLY SUPPORTED
 *
 * @param {any} obj
 */
function contentForQueries(obj) {
	tryContentByQuery(obj.profile, obj.password, null, obj);
}

function tryContentByQuery(profile, password, siteIdentifier, query, successCallback, optionalTransform, execCount) {
	execCount = execCount || 0;

	if (ContentByQueryUtilities.isValidQuery(query)) {
		callContentByQuery(profile, password, query, successCallback, optionalTransform);
	}
	else if (execCount < 100) {
		var newQuery = ContentByQueryUtilities.parseQueryFromDocument(novSelect.config, document.body.innerHTML);

		setTimeout(function () {
			if (novSelect && novSelect.tryContentByQuery) {
				novSelect.tryContentByQuery(profile, password, siteIdentifier, newQuery, successCallback, optionalTransform, execCount + 1);
			}
		}, 500);
	}
}

function callContentByQuery(profile, password, query, successCallback, optionalTransform) {
	// create the global dataLoaded function
	novSelect.dataLoaded = createDataLoadedCallback(novSelect.config, novSelect.baseUrl, query, successCallback, optionalTransform);

	const url = buildContentByQueryUrl(profile, password, null, query);

	utility.loadScript(url);
}


/**
 * Modifies some properties on the global novSelect object
 * and returns an object that is mixed in to the global novSelect object to add
 * additional methods
 */
var createNovSelectMixin = function () {
	novSelect.baseUrl = !novSelect.baseUrl ? "//novselect.ebscohost.com" : novSelect.baseUrl;
	novSelect.baseImageUrl = !novSelect.baseImageUrl ? "//imageserver.ebscohost.com/novelistselect" : novSelect.baseImageUrl;
	novSelect.cache = !novSelect.cache ? true : novSelect.cache;
	novSelect.indent = !novSelect.indent ? false : novSelect.indent;

	return {
		contentForQuery: contentForQuery,
		contentForISBN: contentForISBN,
		contentForQueries: contentForQueries,
		dataForISBN: dataForISBN,
		dataForQuery: dataForQuery,
		clearReact: clearReact,
		baseUrl: window.novSelect.baseUrl,
		tryContentByQuery: tryContentByQuery
	};
};

if (!window.novSelect) {
	window.novSelect = {};
}

window.novSelect = { ...window.novSelect, ...createNovSelectMixin() };



// WEBPACK FOOTER //
// ./src/index.js